package com.schoolconnect.app.feature.academicyears

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogInputSize
import com.schoolconnect.app.designsystem.AppDialogLabelSize
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSuccess
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import org.jetbrains.compose.resources.painterResource

private val AcademicYearViewDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors

@Composable
internal fun AcademicYearViewModal(
    item: AcademicYearListItem,
    onClose: () -> Unit
) {
    val statusColor = if (item.isActive) AppSuccess else AppDanger
    val statusLabel = if (item.isActive) "Active" else "Inactive"

    AppDialogScaffold(
        onDismiss = onClose,
        size = AppDialogSize.Standard,
        colors = AcademicYearViewDialogColors,
        dismissOnClickOutside = true
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AcademicYearViewHeader(item = item, onClose = onClose)
            AppDialogDivider(colors = AcademicYearViewDialogColors)
            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) { compact ->
                val scrollState = rememberScrollState()
                val gap = 16.dp
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 24.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(gap)
                ) {
                    if (compact) {
                        AcademicYearViewField(label = "School", value = item.school)
                        AcademicYearViewField(label = "Start Date", value = item.startDate)
                        AcademicYearViewField(label = "End Date", value = item.endDate)
                        AcademicYearViewField(label = "Academic Year Label", value = item.academicYear)
                        AcademicYearViewField(label = "Academic Year Lifecycle", value = item.status.label)
                        AcademicYearViewField(label = "Status", value = statusLabel, valueColor = statusColor)
                    } else {
                        AcademicYearViewField(label = "School", value = item.school)
                        AcademicYearViewFieldRow(
                            leftLabel = "Start Date",
                            leftValue = item.startDate,
                            rightLabel = "End Date",
                            rightValue = item.endDate
                        )
                        AcademicYearViewFieldRow(
                            leftLabel = "Academic Year Label",
                            leftValue = item.academicYear,
                            rightLabel = "Academic Year Lifecycle",
                            rightValue = item.status.label
                        )
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            AcademicYearViewField(
                                modifier = Modifier.weight(1f),
                                label = "Status",
                                value = statusLabel,
                                valueColor = statusColor
                            )
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }
            }
            AppDialogFooter(
                actions = listOf(
                    AppDialogFooterAction(
                        label = "Close",
                        style = AppDialogActionStyle.Primary,
                        onClick = onClose
                    )
                ),
                colors = AcademicYearViewDialogColors,
                contentPadding = PaddingValues(horizontal = 24.dp)
            )
        }
    }
}

@Composable
private fun AcademicYearViewHeader(
    item: AcademicYearListItem,
    onClose: () -> Unit
) {
    val createdBy = item.createdBy.ifBlank { "—" }
    val updatedBy = item.lastModifiedBy.ifBlank { "—" }
    val createdOn = item.createdOn.ifBlank { "—" }
    val updatedOn = item.updatedOn.ifBlank { "—" }
    val auditText = buildAnnotatedString {
        val labelStyle = SpanStyle(fontWeight = FontWeight.SemiBold, color = AppMuted)
        val valueStyle = SpanStyle(fontWeight = FontWeight.Normal, color = AppMuted)
        withStyle(labelStyle) { append("Created by") }
        withStyle(valueStyle) { append(" $createdBy · $createdOn | ") }
        withStyle(labelStyle) { append("Last modified by") }
        withStyle(valueStyle) { append(" $updatedBy · $updatedOn") }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 24.dp, end = 20.dp, top = 16.dp, bottom = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = 16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = "View Academic Year",
                color = AppPrimary,
                fontSize = 20.sp,
                fontWeight = FontWeight.Bold,
                maxLines = 1
            )
            Text(
                text = "Academic year details and lifecycle information.",
                color = AppMuted,
                fontSize = 13.sp,
                fontWeight = FontWeight.Normal,
                maxLines = 2
            )
            Text(
                text = auditText,
                fontSize = 12.sp,
                lineHeight = 18.sp
            )
        }
        Box(
            modifier = Modifier
                .size(32.dp)
                .clickable(onClick = onClose),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.close_x),
                contentDescription = "Close",
                tint = AppMuted,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
private fun AcademicYearViewFieldRow(
    leftLabel: String,
    leftValue: String,
    rightLabel: String,
    rightValue: String
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        AcademicYearViewField(
            modifier = Modifier.weight(1f),
            label = leftLabel,
            value = leftValue
        )
        AcademicYearViewField(
            modifier = Modifier.weight(1f),
            label = rightLabel,
            value = rightValue
        )
    }
}

@Composable
private fun AcademicYearViewField(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    valueColor: Color? = null
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = label,
            color = AppNavy,
            fontSize = AppDialogLabelSize,
            fontWeight = FontWeight.SemiBold
        )
        Text(
            text = value.ifBlank { "—" },
            color = valueColor ?: AppNavy,
            fontSize = AppDialogInputSize,
            fontWeight = FontWeight.Normal
        )
    }
}
